// ✅ StudentFeeDashboard
// - Same orange/blue theme maintained
// - Icons added for better UI
// - Removed fixed table/input widths
// - Added smooth horizontal scroll for smaller screens

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Checkbox,
  Grid,
  Divider,
  Chip,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentsIcon from "@mui/icons-material/Payments";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import SchoolIcon from "@mui/icons-material/School";
import PaidIcon from "@mui/icons-material/Paid";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ReplayIcon from "@mui/icons-material/Replay";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { API_URL } from "./config";

/* ---------------------- gateway helpers ---------------------- */
function loadScript(src) {
  return new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
    const s = document.createElement("script");
    s.src = src;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function n2(x) {
  const v = Number(x || 0);
  return Number.isFinite(v) ? v : 0;
}

/* --------------------------- page ---------------------------- */
const StudentFeeDashboard = () => {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = {
    p: { xs: 1, sm: 2 },
    backgroundColor: BG,
    minHeight: "100vh",
  };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = {
    p: { xs: 1.25, sm: 2 },
    backgroundColor: BG,
  };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputBase-root": {
      borderRadius: 2,
      backgroundColor: "#fff",
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    whiteSpace: "nowrap",
  };

  const tableWrapSx = {
    ...surfaceSx,
    overflow: "hidden",
  };

  const tableScrollSx = {
    overflowX: "auto",
    overflowY: "hidden",
    width: "100%",
    "&::-webkit-scrollbar": {
      height: 8,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(25,118,210,0.35)",
      borderRadius: 10,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgba(25,118,210,0.08)",
      borderRadius: 10,
    },
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": {
      borderBottom: "1px solid rgba(25,118,210,0.10)",
      py: 0.8,
      whiteSpace: "nowrap",
    },
  });

  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  const token = localStorage.getItem("auth");

  const [logincampusCode, setCampusCode] = useState("");
  const [username, setUsername] = useState("");
  const [studentId, setStudentId] = useState("");
  const [studentkey, setStudentKey] = useState("");

  const [bundles, setBundles] = useState([]);
  const [unbundled, setUnbundled] = useState([]);

  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [amounts, setAmounts] = useState({});

  const [paymentMethod, setPaymentMethod] = useState("");
  const [payBusy, setPayBusy] = useState(false);
  const [payMsg, setPayMsg] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [externalPaymentDialogOpen, setExternalPaymentDialogOpen] =
  useState(false);
  const [externalCart, setExternalCart] = useState(null);
  const [externalPaymentRef, setExternalPaymentRef] = useState("");
  const [externalSubmitBusy, setExternalSubmitBusy] = useState(false);
  const [pendingExternalPayments, setPendingExternalPayments] = useState([]);
  const [pendingPaymentsLoading, setPendingPaymentsLoading] = useState(false);

  const EXTERNAL_CART_STORAGE_KEY = "unifloo_external_payment_cart";

  const normalizePaymentRef = (value) =>
    String(value || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "");
  
  const isValidPaymentRef = (value) => {
    const ref = normalizePaymentRef(value);
  
    return /^[A-Z0-9][A-Z0-9\-\/]{5,49}$/.test(
      ref
    );
  };

  const saveExternalCartLocally = (cart) => {
  localStorage.setItem(
    EXTERNAL_CART_STORAGE_KEY,
    JSON.stringify(cart)
  );
};

  const removeExternalCartLocally = () => {
    localStorage.removeItem(EXTERNAL_CART_STORAGE_KEY);
  };
  
  const restoreExternalCartLocally = () => {
    try {
      const stored = localStorage.getItem(
        EXTERNAL_CART_STORAGE_KEY
      );
  
      if (!stored) return;
  
      const parsed = JSON.parse(stored);
  
      if (
        parsed?.CartId &&
        parsed?.ClientTxnRef &&
        parsed?.Gateway === "EXTERNAL_REDIRECT"
      ) {
        setExternalCart(parsed);
        setExternalPaymentDialogOpen(true);
      }
    } catch (err) {
      console.error("Unable to restore external cart", err);
      removeExternalCartLocally();
    }
  };

  const bKey = (b) => `BUNDLE:${String(b.Semester)}:${Number(b.BundleId)}`;
  const fKey = (f) => `FEE:${Number(f.StudentFeeID)}`;

  const fetchPendingExternalPayments = async () => {
    try {
      setPendingPaymentsLoading(true);
  
      const { data } = await axios.get(
        `${API_URL}/api/payments/student/external-pending`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      setPendingExternalPayments(
        Array.isArray(data?.payments)
          ? data.payments
          : []
      );
    } catch (err) {
      console.error(
        "Failed to load pending external payments",
        err
      );
  
      setPendingExternalPayments([]);
    } finally {
      setPendingPaymentsLoading(false);
    }
  };
  
  const pendingByStudentFeeId = useMemo(() => {
    const map = new Map();
  
    for (const payment of pendingExternalPayments) {
      const lines = Array.isArray(payment.lines)
        ? payment.lines
        : [];
  
      for (const line of lines) {
        const studentFeeId = Number(
          line.StudentFeeID
        );
  
        if (!studentFeeId) continue;
  
        const existing = map.get(studentFeeId) || {
          PendingAmount: 0,
          CartIds: [],
          PaymentReferences: [],
        };
  
        existing.PendingAmount += n2(line.Amount);
  
        if (payment.CartId) {
          existing.CartIds.push(payment.CartId);
        }
  
        if (payment.PaymentReference) {
          existing.PaymentReferences.push(
            payment.PaymentReference
          );
        }
  
        map.set(studentFeeId, existing);
      }
    }
  
    return map;
  }, [pendingExternalPayments]);  

  useEffect(() => {
    restoreExternalCartLocally();
  }, []);

  useEffect(() => {
    if (!token) return;

    try {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decodedToken.campusCode || "");
      setUsername(decodedToken.username || "");
      setStudentId(decodedToken.username || "");
      setStudentKey(decodedToken.studentkey || "");
    } catch (err) {
      console.error("Error decoding token:", err);
      setError("Failed to decode authentication token.");
    }
  }, [token]);

  useEffect(() => {
    if (!studentId || !logincampusCode) return;
  
    const loadPage = async () => {
      await Promise.all([
        fetchFees(),
        fetchPendingExternalPayments(),
      ]);
    };
  
    loadPage();
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, logincampusCode]);

  const fetchFees = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/students/fees`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data || {};
      setBundles(Array.isArray(data.bundles) ? data.bundles : []);
      setUnbundled(Array.isArray(data.unbundled) ? data.unbundled : []);

      setSelectedKeys(new Set());
      setAmounts({});
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to fetch fees for the student.");
    }
  };

  const submitExternalPaymentReference =
    async () => {
      try {
        setError("");
        setPayMsg(null);
  
        if (!externalCart?.CartId) {
          return setError(
            "External payment cart is missing."
          );
        }
  
        const paymentReference =
          normalizePaymentRef(
            externalPaymentRef
          );
  
        if (
          !isValidPaymentRef(
            paymentReference
          )
        ) {
          return setError(
            "Enter a valid Payment Ref No."
          );
        }
  
        setExternalSubmitBusy(true);
  
        const { data } = await axios.post(
          `${API_URL}/api/payments/student/external-payment-reference`,
          {
            CartId: externalCart.CartId,
            PaymentReference:
              paymentReference,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
  
        removeExternalCartLocally();
  
        setExternalPaymentDialogOpen(false);
        setExternalCart(null);
        setExternalPaymentRef("");
  
        setSelectedKeys(new Set());
        setAmounts({});
  
        await Promise.all([
          fetchFees(),
          fetchPendingExternalPayments(),
        ]);
  
        setPayMsg({
          type: "success",
          text:
            data?.message ||
            "Payment submitted. Bank reconciliation is in process.",
        });
      } catch (err) {
        console.error(
          "Failed to submit payment reference",
          err
        );
  
        setError(
          err?.response?.data?.error ||
            "Failed to submit Payment Ref No."
        );
      } finally {
        setExternalSubmitBusy(false);
      }
    };

  const rows = useMemo(() => {
    const bRows = (bundles || []).map((b) => ({
      rowType: "BUNDLE",
      key: bKey(b),
      Semester: String(b.Semester),
      Label: b.Label || "Fee",
      Due: n2(b.Due),
      AllowPartial: !!b.AllowPartial,
      BundleId: Number(b.BundleId),
      SortOrder: Number(b.SortOrder || 0),
      FeeAmount: n2(b.FeeAmount),
      FeePaid: n2(b.FeePaid),
      DiscountAmount: n2(b.DiscountAmount),
      RefundAmount: n2(b.RefundAmount),
      Status: "",
    }));

  const fRows = (unbundled || []).map((f) => {
    const pendingInfo =
      pendingByStudentFeeId.get(
        Number(f.StudentFeeID)
      );
  
    const pendingAmount = n2(
      pendingInfo?.PendingAmount
    );
  
    const actualDue = n2(f.FeeDue);
  
    return {
      rowType: "FEE",
      key: fKey(f),
      Semester: String(f.Semester),
      Label: String(f.FeeType || "Fee"),
  
      StudentFeeID: Number(f.StudentFeeID),
  
      FeeAmount: n2(f.FeeAmount),
      FeePaid: n2(f.FeePaid),
      DiscountAmount: n2(f.DiscountAmount),
      RefundAmount: n2(f.RefundAmount),
  
      ActualDue: actualDue,
      PendingExternalAmount: pendingAmount,
  
      // Amount still available for another payment
      Due: Math.max(
        0,
        actualDue - pendingAmount
      ),
  
      HasPendingExternalPayment:
        pendingAmount > 0,
  
      PendingCartIds:
        pendingInfo?.CartIds || [],
  
      PendingPaymentReferences:
        pendingInfo?.PaymentReferences || [],
  
      Status:
        pendingAmount >= actualDue &&
        actualDue > 0
          ? "Reconciliation in process"
          : String(f.Status || ""),
    };
  });

    const rank = (r) => (r.rowType === "BUNDLE" ? 0 : 1);

    return [...bRows, ...fRows].sort((a, b) => {
      const s = String(a.Semester).localeCompare(String(b.Semester));
      if (s !== 0) return s;

      const r = rank(a) - rank(b);
      if (r !== 0) return r;

      if (a.rowType === "BUNDLE" && b.rowType === "BUNDLE") {
        const so = (a.SortOrder || 0) - (b.SortOrder || 0);
        if (so !== 0) return so;
      }

      return String(a.Label).localeCompare(String(b.Label));
    });
  }, [bundles, unbundled, pendingByStudentFeeId]);

  const selectedCount = selectedKeys.size;

  const totalFeeAmount = useMemo(() => rows.reduce((s, r) => s + n2(r.FeeAmount), 0), [rows]);
  const totalFeePaid = useMemo(() => rows.reduce((s, r) => s + n2(r.FeePaid), 0), [rows]);
  const totalDiscount = useMemo(() => rows.reduce((s, r) => s + n2(r.DiscountAmount), 0), [rows]);
  const totalRefund = useMemo(() => rows.reduce((s, r) => s + n2(r.RefundAmount), 0), [rows]);
  const totalDue = useMemo(() => rows.reduce((s, r) => s + n2(r.Due), 0), [rows]);

  const selectedPayTotal = useMemo(() => {
    let s = 0;
    selectedKeys.forEach((k) => {
      s += n2(amounts[k]);
    });
    return s;
  }, [selectedKeys, amounts]);

  const toggleRow = (r) => {
    if (r.HasPendingExternalPayment) {
      setError(
        "This fee already has a payment under reconciliation."
      );
      return;
    }
    const k = r.key;
    const due = n2(r.Due);

    setSelectedKeys((prev) => {
      const next = new Set(prev);

      if (next.has(k)) {
        next.delete(k);
        setAmounts((a) => {
          const na = { ...a };
          delete na[k];
          return na;
        });
      } else if (due > 0) {
        next.add(k);
        setAmounts((a) => ({ ...a, [k]: due }));
      }

      return next;
    });
  };

  const onAmountChange = (r, value) => {
    const k = r.key;
    const entered = n2(value);
    const due = n2(r.Due);

    if (entered < 0) return setError("Payment amount cannot be negative.");

    if (r.rowType === "BUNDLE") {
      if (r.AllowPartial) {
        if (entered > due + 0.009) return setError(`Amount cannot exceed Due (₹${due}).`);
      } else if (Math.abs(entered - due) > 0.009) {
        return setError(`This fee must be paid in full: ₹${due}`);
      }
    } else if (entered > due + 0.009) {
      return setError(`Amount cannot exceed Due (₹${due}).`);
    }

    setError("");
    setAmounts((a) => ({ ...a, [k]: entered }));
  };

  const buildCartItems = async () => {
    const items = [];

    for (const r of rows) {
      if (!selectedKeys.has(r.key)) continue;

      const amt = n2(amounts[r.key]);
      if (!(amt > 0)) continue;

      if (r.rowType === "BUNDLE") {
        const { data } = await axios.get(`${API_URL}/api/fees/student/bundle-lines`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            BundleId: r.BundleId,
            Semester: r.Semester,
            Amount: amt,
          },
        });

        const arr = Array.isArray(data) ? data : data?.Items || [];

        for (const it of arr) {
          const sid = Number(it.StudentFeeID);
          const a = n2(it.Amount);
          if (sid && a > 0) items.push({ StudentFeeID: sid, Amount: a });
        }
      } else {
        items.push({ StudentFeeID: r.StudentFeeID, Amount: amt });
      }
    }

    const map = new Map();

    for (const it of items) {
      map.set(it.StudentFeeID, (map.get(it.StudentFeeID) || 0) + n2(it.Amount));
    }

    return [...map.entries()].map(([StudentFeeID, Amount]) => ({
      StudentFeeID,
      Amount,
    }));
  };

  const handlePayNow = async () => {
    try {
      setError("");
      setPayMsg(null);

      if (!selectedCount) return setError("Select at least one fee row to pay.");
      if (paymentMethod !== "Online") {
        return setError("Select Payment Method = Online to use payment gateway.");
      }

      for (const r of rows) {
        if (!selectedKeys.has(r.key)) continue;

        if (r.HasPendingExternalPayment) {
        return setError(
          `"${r.Label}" already has a payment under reconciliation.`);
        }

        const amt = n2(amounts[r.key]);
        const due = n2(r.Due);

        if (!(amt > 0)) return setError("Each selected row must have a valid amount (> 0).");

        if (r.rowType === "BUNDLE") {
          if (r.AllowPartial) {
            if (amt > due + 0.009) return setError(`Amount cannot exceed Due (₹${due}).`);
          } else if (Math.abs(amt - due) > 0.009) {
            return setError(`"${r.Label}" must be paid in full (₹${due}).`);
          }
        } else if (amt > due + 0.009) {
          return setError(`Amount cannot exceed Due (₹${due}) for ${r.Label}.`);
        }
      }

      setPayBusy(true);
      setPayMsg({
        type: "info",
        text: `Starting online payment for ₹${selectedPayTotal}...`,
      });

      const Items = await buildCartItems();
      if (!Items.length) return setError("No payable items were generated. Check bundle-lines endpoint.");

      const idem = `CART_${studentkey}_${Date.now()}`;

      const { data } = await axios.post(
        `${API_URL}/api/payments/student/create-cart-order`,
        {
          Mode: "LIVE",
          IdempotencyKey: idem,
          Items,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (data.Gateway === "EXTERNAL_REDIRECT") {
        const redirectUrl =
          data.RedirectUrl ||
          data.ExternalRedirectUrl;
      
        if (!redirectUrl) {
          throw new Error(
            "Redirect URL is not configured for this gateway."
          );
        }
      
        if (
          !/^https:\/\//i.test(
            String(redirectUrl)
          )
        ) {
          throw new Error(
            "Invalid redirect URL. Only https:// URLs are allowed."
          );
        }
      
        const pendingCart = {
          Gateway: "EXTERNAL_REDIRECT",
          CartId: Number(data.CartId),
          ClientTxnRef: String(
            data.ClientTxnRef || ""
          ),
          Amount: n2(data.Amount),
          Currency: data.Currency || "INR",
          RedirectUrl: redirectUrl,
        };
      
        setExternalCart(pendingCart);
        saveExternalCartLocally(pendingCart);
      
        setExternalPaymentRef("");
        setExternalPaymentDialogOpen(true);
      
        const win = window.open(
          redirectUrl,
          "_blank",
          "noopener,noreferrer"
        );
      
        if (!win) {
          setPayMsg({
            type: "warning",
            text:
              "The payment popup was blocked. Use the Open Bank Portal button to continue.",
          });
      
          return;
        }
      
        setPayMsg({
          type: "info",
          text:
            "Complete the payment in the bank portal, then return here and enter the Payment Ref No.",
        });
      
        return;
      }

      if (data.Gateway === "RAZORPAY") {
        const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
        if (!ok) throw new Error("Failed to load Razorpay Checkout.");

        const rzp = new window.Razorpay({
          key: data.RazorpayKeyId,
          amount: Math.round(n2(data.Amount) * 100),
          currency: data.Currency || "INR",
          name: `${logincampusCode} Fees` || "Fees",
          description: data.ClientTxnRef,
          order_id: data.RazorpayOrderId,
          handler: async (resp) => {
            try {
              await axios.post(
                `${API_URL}/api/payments/student/confirm-cart`,
                {
                  CartId: data.CartId,
                  razorpay_order_id: resp.razorpay_order_id,
                  razorpay_payment_id: resp.razorpay_payment_id,
                  razorpay_signature: resp.razorpay_signature,
                },
                {
                  headers: { Authorization: `Bearer ${token}` },
                }
              );

              setPayMsg({
                type: "success",
                text: "Payment received. Updating fee status...",
              });

              await fetchFees();
              setSuccess(true);
            } catch (e) {
              console.error("confirm-cart failed", e);
              setPayMsg({
                type: "error",
                text: e?.response?.data?.error || "Payment confirmed but server update failed.",
              });
            }
          },
          modal: {
            ondismiss: () =>
              setPayMsg({
                type: "warning",
                text: "Payment window closed.",
              }),
          },
          prefill: { name: username },
          notes: {
            StudentKey: String(studentkey),
            CampusCode: logincampusCode,
          },
          theme: { color: THEME_ORANGE },
        });

        rzp.open();
        return;
      }

      if (data.Gateway === "PAYTM") {
        const url = `https://securegw.paytm.in/merchantpgpui/checkoutjs/merchants/${data.PaytmMid}.js`;
        const ok = await loadScript(url);
        if (!ok) throw new Error("Failed to load Paytm CheckoutJS.");

        const config = {
          root: "",
          flow: "DEFAULT",
          data: {
            orderId: data.PaytmOrderId,
            token: data.PaytmTxnToken,
            tokenType: "TXN_TOKEN",
            amount: String(data.Amount),
          },
        };

        setPayMsg({
          type: "info",
          text: "Opening Paytm...",
        });

        await window.Paytm.CheckoutJS.init(config);
        window.Paytm.CheckoutJS.invoke();
        return;
      }

      if (data.Gateway === "BILLDESK") {
        const rd = data.Redirect;
        if (!rd?.url || !rd?.fields) throw new Error("BillDesk redirect payload missing.");

        const form = document.createElement("form");
        form.method = "POST";
        form.action = rd.url;

        Object.entries(rd.fields).forEach(([k, v]) => {
          const inp = document.createElement("input");
          inp.type = "hidden";
          inp.name = k;
          inp.value = String(v ?? "");
          form.appendChild(inp);
        });

        document.body.appendChild(form);
        form.submit();
        return;
      }

      throw new Error("Unsupported gateway from server.");
    } catch (err) {
      console.error(err);
      setPayMsg(null);
      setError(err?.response?.data?.error || err.message || "Payment failed");
    } finally {
      setPayBusy(false);
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <ReceiptLongIcon />
            <Box>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
                Student Fee Dashboard
              </Typography>
              <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
                Select fee items and pay online
              </Typography>
            </Box>
          </Stack>

          <Chip
            icon={<AccountBalanceWalletIcon />}
            label={`Payable ₹${selectedPayTotal}`}
            sx={{
              bgcolor: "rgba(255,255,255,0.18)",
              color: "#fff",
              fontWeight: 900,
              "& .MuiChip-icon": { color: "#fff" },
            }}
          />
        </Box>

        <Box sx={bodySx}>
          {error && (
            <Alert
              severity="error"
              icon={<WarningAmberIcon />}
              onClose={() => setError("")}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {payMsg && (
            <Alert
              severity={payMsg.type}
              onClose={() => setPayMsg(null)}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              {payMsg.text}
            </Alert>
          )}

          {success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Payment successful!
              </Alert>
            </Snackbar>
          )}

          {/* Add it here */}
          {externalCart &&
            !externalPaymentDialogOpen && (
              <Alert
                severity="warning"
                icon={
                  <HourglassTopIcon />
                }
                sx={{
                  mb: 2,
                  borderRadius: 2,
                }}
                action={
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() =>
                      setExternalPaymentDialogOpen(
                        true
                      )
                    }
                    sx={{
                      fontWeight: 900,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Complete
                  </Button>
                }
              >
                An external payment was started
                for ₹
                {n2(externalCart.Amount)}.
                Enter the Payment Ref No after
                completing the bank payment.
              </Alert>
            )}

          {pendingExternalPayments.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                ...sectionSx,
                mb: 2,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <HourglassTopIcon
                  sx={{ color: THEME_ORANGE }}
                />

                <Typography
                  sx={{
                    fontWeight: 900,
                    color: "#1565C0",
                  }}
                >
                  Payments Under Reconciliation
                </Typography>
              </Stack>
                
              <Alert
                severity="info"
                sx={{ mb: 1.5 }}
              >
                These payments were reported as
                successful and are being matched
                with the college bank report. The
                associated fee items cannot be paid
                again while reconciliation is in
                process.
              </Alert>
                
              <Stack spacing={1}>
                {pendingExternalPayments.map(
                  (payment) => (
                    <Paper
                      key={payment.CartId}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        borderColor:
                          "rgba(240,120,40,0.35)",
                        backgroundColor:
                          "rgba(240,120,40,0.04)",
                      }}
                    >
                      <Grid
                        container
                        spacing={1}
                        alignItems="center"
                      >
                        <Grid
                          item
                          xs={12}
                          sm={3}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            Amount
                          </Typography>
                    
                          <Typography fontWeight={900}>
                            ₹
                            {n2(
                              payment.TotalAmount
                            )}
                          </Typography>
                        </Grid>
                          
                        <Grid
                          item
                          xs={12}
                          sm={4}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            Payment Ref No
                          </Typography>
                          
                          <Typography fontWeight={800}>
                            {
                              payment.PaymentReference
                            }
                          </Typography>
                        </Grid>
                          
                        <Grid
                          item
                          xs={12}
                          sm={3}
                        >
                          <Chip
                            size="small"
                            icon={<HourglassTopIcon />}
                            label="Reconciliation in process"
                            sx={{
                              fontWeight: 800,
                              color: "#E65100",
                              backgroundColor:
                                "rgba(240,120,40,0.12)",
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  )
                )}
              </Stack>
            </Paper>
          )}

          <Paper elevation={0} sx={tableWrapSx}>
            <Box
              sx={{
                px: 2,
                py: 1.2,
                borderBottom: "1px solid rgba(25,118,210,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <PaymentsIcon sx={{ color: THEME_ORANGE }} />
                <Typography sx={{ fontWeight: 900, color: "#1565C0" }}>
                  Fee Details
                </Typography>
              </Stack>

              <Chip
                size="small"
                label={`${selectedCount} Selected`}
                sx={{
                  fontWeight: 800,
                  bgcolor: "rgba(240,120,40,0.10)",
                  color: THEME_ORANGE,
                  border: "1px solid rgba(240,120,40,0.25)",
                }}
              />
            </Box>

            <Box sx={tableScrollSx}>
              <TableContainer component="div">
                <Table
                  size="small"
                  sx={{
                    width: "max-content",
                    minWidth: "100%",
                    tableLayout: "auto",
                  }}
                >
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      <TableCell>Select</TableCell>
                      <TableCell>#</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.7} alignItems="center">
                          <SchoolIcon fontSize="small" />
                          <span>Semester</span>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.7} alignItems="center">
                          <ReceiptLongIcon fontSize="small" />
                          <span>Fee Item</span>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.7} alignItems="center">
                          <CurrencyRupeeIcon fontSize="small" />
                          <span>Fee</span>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.7} alignItems="center">
                          <PaidIcon fontSize="small" />
                          <span>Paid</span>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.7} alignItems="center">
                          <LocalOfferIcon fontSize="small" />
                          <span>Discount</span>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.7} alignItems="center">
                          <ReplayIcon fontSize="small" />
                          <span>Refund</span>
                        </Stack>
                      </TableCell>
                      <TableCell>Due</TableCell>
                      <TableCell>Payment Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map((r, idx) => {
                      const checked = selectedKeys.has(r.key);
                      const due = n2(r.Due);
                      const amt = amounts[r.key] ?? "";

                      return (
                        <TableRow key={r.key} sx={zebraRowSx(idx)}>
                          <TableCell>
                            {/*<Tooltip title={due <= 0 ? "No due amount" : "Select to pay"}>*/}
                            <Tooltip title={r.HasPendingExternalPayment ? "Payment reported. Reconciliation is in process." : due <= 0 ? "No payable amount" : "Select to pay"} >
                              <span>
                                <Checkbox
                                  sx={checkboxSx}
                                  checked={checked}
                                  disabled={due <= 0 || r.HasPendingExternalPayment}
                                  onChange={() => toggleRow(r)}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>

                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{r.Semester}</TableCell>

                          <TableCell sx={{ fontWeight: 900 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <span>{r.Label}</span>
                              
                            </Stack>
                          </TableCell>

                          <TableCell>₹{r.FeeAmount}</TableCell>
                          <TableCell sx={{ color: "#2E7D32", fontWeight: 800 }}>
                            ₹{r.FeePaid}
                          </TableCell>
                          <TableCell sx={{ color: THEME_ORANGE, fontWeight: 800 }}>
                            ₹{r.DiscountAmount}
                          </TableCell>
                          <TableCell sx={{ color: "#D11A1C", fontWeight: 800 }}>
                            ₹{r.RefundAmount}
                          </TableCell>

                          <TableCell sx={{ fontWeight: 900, color: "#D32F2F" }}>
                            ₹{due}
                          </TableCell>

                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              sx={{
                                ...inputSx,
                                "& .MuiInputBase-input": {
                                  py: 0.8,
                                },
                              }}
                              disabled={!checked || due <= 0}
                              value={amt}
                              inputProps={{
                                min: 0,
                                max: due,
                                step: "0.01",
                              }}
                              onChange={(e) => onAmountChange(r, e.target.value)}
                              helperText={
                                checked
                                  ? r.rowType === "BUNDLE"
                                    ? r.AllowPartial
                                      ? `Min 1, Max ₹${due}`
                                      : `Pay full: ₹${due}`
                                    : `Max ₹${due}`
                                  : ""
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {r.HasPendingExternalPayment ? (
                              <Stack spacing={0.5}>
                                <Chip
                                  size="small"
                                  icon={<HourglassTopIcon />}
                                  label="Paid — reconciliation in process"
                                  sx={{
                                    fontWeight: 800,
                                    color: "#E65100",
                                    backgroundColor:
                                      "rgba(240,120,40,0.12)",
                                  }}
                                />

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  ₹{r.PendingExternalAmount} reported
                                </Typography>
                              </Stack>
                            ) : (
                              <Chip
                                size="small"
                                label={r.Status || "Pending"}
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    <TableRow
                      sx={{
                        backgroundColor: "rgba(240,120,40,0.06)",
                        "& td": {
                          borderTop: "1px solid rgba(25,118,210,0.12)",
                          py: 1,
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      <TableCell colSpan={4} sx={{ fontWeight: 900 }}>
                        Subtotal
                      </TableCell>
                    
                      <TableCell sx={{ fontWeight: 900 }}>
                        ₹{totalFeeAmount}
                      </TableCell>
                    
                      <TableCell sx={{ fontWeight: 900, color: "#2E7D32" }}>
                        ₹{totalFeePaid}
                      </TableCell>
                    
                      <TableCell sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                        ₹{totalDiscount}
                      </TableCell>
                    
                      <TableCell sx={{ fontWeight: 900, color: "#D11A1C" }}>
                        ₹{totalRefund}
                      </TableCell>
                    
                      <TableCell sx={{ fontWeight: 900, color: "#D32F2F" }}>
                        ₹{totalDue}
                      </TableCell>
                    
                      <TableCell sx={{ fontWeight: 900, color: "#1565C0" }}>
                        ₹{selectedPayTotal}
                      </TableCell>
                    
                      <TableCell sx={{ fontWeight: 900 }}>
                        
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
            <Box sx={{ pl: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <CreditCardIcon sx={{ color: THEME_ORANGE }} />
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                  Payment
                </Typography>
              </Stack>

              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={paymentMethod}
                      label="Payment Method"
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      <MenuItem value="Online">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CreditCardIcon fontSize="small" />
                          <span>Online</span>
                        </Stack>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<ShoppingCartCheckoutIcon />}
                    sx={primaryBtnSx}
                    onClick={handlePayNow}
                    disabled={
                      payBusy ||
                      selectedCount === 0 ||
                      paymentMethod !== "Online" ||
                      selectedPayTotal <= 0
                    }
                  >
                    {payBusy ? "Starting..." : `Pay Now ₹${selectedPayTotal}`}
                  </Button>
                </Grid>
              </Grid>

              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1.2 }}>
                <InfoOutlinedIcon sx={{ fontSize: 18, color: "#1565C0", mt: "2px" }} />
                <Typography sx={{ fontSize: "0.82rem", color: "rgba(0,0,0,0.62)" }}>
                  Bundle payments are shown as one line item, but are allocated internally to
                  underlying fee lines.
                </Typography>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Paper>

      <Dialog
        open={externalPaymentDialogOpen}
        onClose={() => {
          // Do not delete the cart.
          // Student can return later.
          setExternalPaymentDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            color: "#1565C0",
          }}
        >
          Complete External Payment
        </DialogTitle>
        
        <DialogContent dividers>
          <Alert
            severity="info"
            sx={{ mb: 2 }}
          >
            Complete the payment on the bank
            portal. After the bank displays
            <strong> SUCCESSFUL</strong>, enter
            the Payment Ref No shown on the
            transaction confirmation.
          </Alert>
        
          {externalCart && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                borderColor: BORDER,
              }}
            >
              <Stack spacing={1.5}>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    UniFLOO Cart Reference
                  </Typography>
            
                  <Typography
                    fontWeight={800}
                    sx={{
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      lineHeight: 1.35,
                    }}
                  >
                    {externalCart.ClientTxnRef}
                  </Typography>
                </Box>
                  
                <Divider />
                  
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Payment amount
                  </Typography>
                  
                  <Typography
                    fontWeight={900}
                    sx={{ fontSize: "1.15rem" }}
                  >
                    ₹{n2(externalCart.Amount)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          )}

          <TextField
            fullWidth
            required
            label="Payment Ref No"
            placeholder="Example: BIC5YV60RQ46CT"
            value={externalPaymentRef}
            onChange={(event) =>
              setExternalPaymentRef(
                event.target.value
                  .toUpperCase()
                  .replace(/\s+/g, "")
              )
            }
            inputProps={{
              maxLength: 50,
              autoComplete: "off",
            }}
            helperText={
              "Enter the Payment Ref No shown only after the bank displays a successful transaction."
            }
            sx={inputSx}
          />

          <Alert
            severity="warning"
            sx={{ mt: 2 }}
          >
            Submitting a reference does not
            immediately post the payment to the
            college ledger. It will be verified
            against the bank reconciliation
            report.
          </Alert>
        </DialogContent>
          
        <DialogActions
          sx={{
            p: 2,
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Button
            startIcon={<OpenInNewIcon />}
            onClick={() => {
              if (
                externalCart?.RedirectUrl
              ) {
                window.open(
                  externalCart.RedirectUrl,
                  "_blank",
                  "noopener,noreferrer"
                );
              }
            }}
          >
            Open Bank Portal
          </Button>
          
          <Button
            onClick={() =>
              setExternalPaymentDialogOpen(false)
            }
          >
            Enter Later
          </Button>
          
          <Button
            variant="contained"
            startIcon={
              externalSubmitBusy ? (
                <CircularProgress
                  size={18}
                  color="inherit"
                />
              ) : (
                <CheckCircleOutlineIcon />
              )
            }
            disabled={
              externalSubmitBusy ||
              !isValidPaymentRef(
                externalPaymentRef
              )
            }
            onClick={
              submitExternalPaymentReference
            }
            sx={primaryBtnSx}
          >
            Submit for Reconciliation
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default StudentFeeDashboard;